import React, { useState } from 'react';
import dayjs from 'dayjs';
import { Col, Row } from '@/lib/utils';
import { DemiLightText, MediumText, RegularText } from '@/lib/Text';
import { Button } from '@/components/ui/button';
import SvgIcon from '@/components/SvgIcon';
import { getCoachWorkRoutines } from '@/features/coachWorkRoutine/api';
import { getCoachWorkExceptions } from '@/features/coachWorkException/api';
import {
    CoachWorkRoutine,
    WeeklyRoutineItem,
} from '@/features/coachWorkRoutine/types';
import { WorkException } from '@/features/coachWorkException/types';
import { DAYS, ExceptionExclude, ExceptionInclude, WeekSchedule } from '../types/workingHours';

type CopyScope = 'routine' | 'exception' | 'all';

type Props = {
    centerId: string;
    year: number;
    month: number;
    coachIds: string[]; // 현재 월에 존재하는 강사 ID (해당 강사만 덮어씀)
    onCancel: () => void;
    onApplied: (result: {
        weekScheduleMap?: Record<string, WeekSchedule>;
        exceptionIncludeMap?: Record<string, ExceptionInclude[]>;
        exceptionExcludeMap?: Record<string, ExceptionExclude[]>;
    }) => void;
};

// 복붙 방지를 위해 Modal 내에서 재정의한 매퍼 (기존 ModalCoachWorkingHours 로직과 동일)
const DOW_TO_DAY: Record<number, (typeof DAYS)[number]> = {
    0: '일',
    1: '월',
    2: '화',
    3: '수',
    4: '목',
    5: '금',
    6: '토',
};

const hhmmToIso = (hhmm: string | null): string => {
    if (!hhmm) return '';
    const [h, m] = hhmm.split(':');
    return dayjs().hour(parseInt(h, 10)).minute(parseInt(m, 10)).second(0).millisecond(0).toISOString();
};

const buildDefaultWeek = (): WeekSchedule => ({
    월: { enabled: false, timeSlots: [] },
    화: { enabled: false, timeSlots: [] },
    수: { enabled: false, timeSlots: [] },
    목: { enabled: false, timeSlots: [] },
    금: { enabled: false, timeSlots: [] },
    토: { enabled: false, timeSlots: [] },
    일: { enabled: false, timeSlots: [] },
});

const apiRoutineToWeekSchedule = (weeklyRoutine: WeeklyRoutineItem[] | null): WeekSchedule => {
    const schedule = buildDefaultWeek();
    if (!weeklyRoutine) return schedule;
    weeklyRoutine.forEach((item) => {
        const day = DOW_TO_DAY[item.dayOfWeek];
        if (!day) return;
        schedule[day] = {
            enabled: item.isWorking,
            timeSlots: (item.timeSlots ?? []).map((slot) => ({
                startTime: hhmmToIso(slot.startTime),
                endTime: hhmmToIso(slot.endTime),
            })),
        };
    });
    return schedule;
};

/**
 * 이전 달 exceptions 데이터를 현재 달로 "날짜 이동"한다.
 * 예: 3/5 → 4/5. 해당 날짜가 현재 월에 존재하지 않으면 스킵 (예: 3/31 → 4월은 30일까지 → 스킵).
 */
const shiftExceptionDateToTargetMonth = (srcDate: string, targetYear: number, targetMonth: number): string | null => {
    const day = dayjs(srcDate).date();
    const candidate = dayjs(`${targetYear}-${String(targetMonth).padStart(2, '0')}-01`).date(day);
    if (candidate.month() + 1 !== targetMonth) return null; // overflow
    return candidate.format('YYYY-MM-DD');
};

const apiExceptionsToUIShifted = (
    exceptions: WorkException[] | null,
    targetYear: number,
    targetMonth: number,
) => {
    const includes: ExceptionInclude[] = [];
    const excludes: ExceptionExclude[] = [];
    if (!exceptions) return { includes, excludes };

    exceptions.forEach((ex, idx) => {
        const srcDate = dayjs(ex.startTime).format('YYYY-MM-DD');
        const shifted = shiftExceptionDateToTargetMonth(srcDate, targetYear, targetMonth);
        if (!shifted) return;

        if (ex.exceptionType === 'include') {
            // 시간은 이전 달 값 그대로 유지하되, 날짜만 현재 달로
            const startHHmm = dayjs(ex.startTime).format('HH:mm');
            const endHHmm = dayjs(ex.endTime).format('HH:mm');
            includes.push({
                id: `copy-inc-${idx}-${Date.now()}`,
                date: shifted,
                startTime: hhmmToIso(startHHmm),
                endTime: hhmmToIso(endHHmm),
                memo: ex.memo || '',
                isSaved: true,
                isMemoEditing: false,
            });
        } else {
            excludes.push({
                id: `copy-exc-${idx}-${Date.now()}`,
                date: shifted,
                memo: ex.memo || '',
                isSaved: true,
                isMemoEditing: false,
            });
        }
    });

    return { includes, excludes };
};

export const CopyFromPrevMonthDialog = ({ centerId, year, month, coachIds, onCancel, onApplied }: Props) => {
    const [scope, setScope] = useState<CopyScope>('all');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const prevDate = dayjs(`${year}-${String(month).padStart(2, '0')}-01`).subtract(1, 'month');
    const prevYear = String(prevDate.year());
    const prevMonth = String(prevDate.month() + 1);

    const handleConfirm = async () => {
        if (!centerId) return;
        setLoading(true);
        setError(null);
        try {
            const coachIdSet = new Set(coachIds);
            const result: Parameters<typeof onApplied>[0] = {};

            if (scope === 'routine' || scope === 'all') {
                const res = await getCoachWorkRoutines({ centerId, year: prevYear, month: prevMonth });
                const prevRoutines = res.data?.coachWorkRoutines ?? [];
                const weekScheduleMap: Record<string, WeekSchedule> = {};
                prevRoutines.forEach((coach: CoachWorkRoutine) => {
                    if (!coachIdSet.has(coach.coachId)) return;
                    weekScheduleMap[coach.coachId] = apiRoutineToWeekSchedule(coach.weeklyRoutine);
                });
                result.weekScheduleMap = weekScheduleMap;
            }

            if (scope === 'exception' || scope === 'all') {
                const res = await getCoachWorkExceptions({ centerId, year: prevYear, month: prevMonth });
                const prevExceptions = res.data?.coachWorkExceptions ?? [];
                const includeMap: Record<string, ExceptionInclude[]> = {};
                const excludeMap: Record<string, ExceptionExclude[]> = {};
                prevExceptions.forEach((coach) => {
                    if (!coachIdSet.has(coach.coachId)) return;
                    const { includes, excludes } = apiExceptionsToUIShifted(coach.exceptions, year, month);
                    includeMap[coach.coachId] = includes;
                    excludeMap[coach.coachId] = excludes;
                });
                result.exceptionIncludeMap = includeMap;
                result.exceptionExcludeMap = excludeMap;
            }

            onApplied(result);
        } catch (e) {
            console.error(e);
            setError('이전 달 데이터를 불러오지 못했습니다');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-[99998] bg-black/30 flex items-center justify-center"
            onClick={(e) => {
                if (e.currentTarget === e.target && !loading) onCancel();
            }}
        >
            <div
                className="bg-white rounded-[20px] shadow-lg w-[480px]"
                onClick={(e) => e.stopPropagation()}
            >
                <Col className="p-[24px]">
                    <Row className="items-center justify-between mb-[6px]">
                        <MediumText fontSize={20} color="#1A1A1A">
                            이전 달 설정 복사
                        </MediumText>
                        <div className="cursor-pointer p-[6px]" onClick={loading ? undefined : onCancel}>
                            <SvgIcon name="SvgX" size={14} stroke="#6B7280" strokeWidth={2} />
                        </div>
                    </Row>
                    <RegularText fontSize={13} color="#6B7280" className="mb-[20px]">
                        {prevDate.format('YYYY년 M월')} 설정을 {year}년 {month}월로 복사합니다. 기존 설정은
                        선택한 항목에 한해 덮어쓰기됩니다.
                    </RegularText>

                    <Col className="gap-[8px] mb-[16px]">
                        <ScopeOption
                            active={scope === 'all'}
                            onClick={() => setScope('all')}
                            title="전체 복사"
                            desc="기본 근무 + 예외 근무 모두 복사"
                        />
                        <ScopeOption
                            active={scope === 'routine'}
                            onClick={() => setScope('routine')}
                            title="기본 근무만"
                            desc="요일별 근무 여부/시간만 복사"
                        />
                        <ScopeOption
                            active={scope === 'exception'}
                            onClick={() => setScope('exception')}
                            title="예외 근무만"
                            desc="추가 근무(IN) / 근무 제외(OUT) 날짜만 복사 — 일자는 동일 날짜로 이동"
                        />
                    </Col>

                    <DemiLightText fontSize={13} color="#6B7280">
                        · 이전 달에 존재하지 않던 강사는 스킵됩니다
                        <br />· 이전 달 3월 31일처럼 현재 달에 없는 날짜의 예외는 자동 제외됩니다
                    </DemiLightText>

                    {error && (
                        <RegularText fontSize={13} color="#D93043" className="mt-[10px]">
                            {error}
                        </RegularText>
                    )}

                    <Row className="justify-end gap-[8px] mt-[20px]">
                        <Button
                            variant="ghost"
                            className="h-[40px] w-[96px] rounded-lg bg-[#F4F5F7] hover:bg-[#E8EAED]"
                            onClick={loading ? undefined : onCancel}
                        >
                            <RegularText fontSize={14}>취소</RegularText>
                        </Button>
                        <Button
                            variant="default"
                            className={`h-[40px] w-[120px] rounded-lg bg-[#3D73DD] hover:bg-[#3D73DD]/90 text-white ${
                                loading ? 'opacity-60 cursor-not-allowed' : ''
                            }`}
                            onClick={loading ? undefined : handleConfirm}
                        >
                            <RegularText fontSize={14} color="#FFFFFF">
                                {loading ? '불러오는 중...' : '복사하기'}
                            </RegularText>
                        </Button>
                    </Row>
                </Col>
            </div>
        </div>
    );
};

const ScopeOption = ({
    active,
    onClick,
    title,
    desc,
}: {
    active: boolean;
    onClick: () => void;
    title: string;
    desc: string;
}) => (
    <div
        className={`flex items-start gap-[10px] p-[14px] rounded-[12px] cursor-pointer border border-solid transition-colors ${
            active ? 'border-[#3D73DD] bg-[#F2F6FE]' : 'border-[#E6E8EA] hover:border-[#D1D5DB]'
        }`}
        onClick={onClick}
    >
        <div
            className={`w-[18px] h-[18px] rounded-full border-[2px] flex items-center justify-center mt-[2px] ${
                active ? 'border-[#3D73DD]' : 'border-[#D1D5DB]'
            }`}
        >
            {active && <div className="w-[8px] h-[8px] rounded-full bg-[#3D73DD]" />}
        </div>
        <Col>
            <MediumText fontSize={14} color="#1A1A1A">
                {title}
            </MediumText>
            <DemiLightText fontSize={13} color="#6B7280" className="mt-[2px]">
                {desc}
            </DemiLightText>
        </Col>
    </div>
);
